import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, authStorage, getApiData, normalizeAuthUser, normalizeLoginData, setUnauthorizedHandler } from '../api';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(authStorage.getToken());
  const [user, setUser] = useState(authStorage.getUser());
  const [bootstrapping, setBootstrapping] = useState(Boolean(authStorage.getToken()));

  const clearSession = () => {
    authStorage.clearSession();
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      if (window.location.pathname.startsWith('/admin') && !['/admin/login', '/admin/verify-otp'].includes(window.location.pathname)) {
        window.location.replace('/admin/login?expired=1');
      }
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      const savedToken = authStorage.getToken();
      if (!savedToken) {
        if (isMounted) setBootstrapping(false);
        return;
      }

      try {
        const response = await api.me();
        if (!isMounted) return;
        const userData = normalizeAuthUser(getApiData(response));
        authStorage.setUser(userData);
        setUser(userData);
        setToken(savedToken);
      } catch {
        if (!isMounted) return;
        clearSession();
      } finally {
        if (isMounted) setBootstrapping(false);
      }
    };

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const response = await api.login(email, password);
    const data = normalizeLoginData(getApiData(response));

    if (data?.otpSessionId || data?.requiresOtp) {
      return { ...data, requiresOtp: true };
    }

    if (data?.token) {
      throw new Error('Backend is not using OTP login. Stop the old backend and run: dotnet run');
    }

    throw new Error('Email verification could not be started. Please try again.');
  };

  const verifyOtp = async (otpSessionId, code) => {
    const response = await api.verifyOtp(otpSessionId, code);
    const data = normalizeLoginData(getApiData(response));

    if (!data?.token) {
      throw new Error('Invalid verification response. Please try again.');
    }

    authStorage.setToken(data.token);
    authStorage.setUser(data.user);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const resendOtp = async (otpSessionId) => {
    const response = await api.resendOtp(otpSessionId);
    return getApiData(response) ?? response?.data ?? {};
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      // no-op
    }
    clearSession();
    window.location.replace('/admin/login?loggedOut=1');
  };

  const refreshUser = async () => {
    const response = await api.me();
    const userData = normalizeAuthUser(getApiData(response));
    authStorage.setUser(userData);
    setUser(userData);
    return userData;
  };

  const value = useMemo(
    () => ({
      token,
      user,
      bootstrapping,
      isAuthenticated: Boolean(token),
      login,
      verifyOtp,
      resendOtp,
      logout,
      refreshUser,
    }),
    [token, user, bootstrapping],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
